import { useEffect, useMemo } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { MySelect } from "../../../components/ui/Select";
import { cn } from "../../../lib/utils";
import { Vehicle } from "../@types";
import { useGetVehiclesMutation } from "../vehicleApiSlice";

interface VehicleDropdownProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  error?: string;
}

interface VehicleOption {
  value: string;
  label: string;
}

const VehicleDropdownMenu: React.FC<VehicleDropdownProps> = ({
  value,
  onChange,
  className,
  disabled,
  placeholder = "Select a vehicle",
  name = "vehicle",
  id = "vehicle",
  showLabel = true,
  error,
}) => {
  const [
    getVehicles,
    { data: vehicleData, isLoading, isError, error: errorData },
  ] = useGetVehiclesMutation();

  const vehicles = useMemo(() => {
    return vehicleData?.data.data || [];
  }, [vehicleData?.data.data]);

  useEffect(() => {
    getVehicles({
      pageNumber: 0,
      pageSize: 100,
    });
  }, [getVehicles]);

  const vehicleTypeOptions: VehicleOption[] = useMemo(() => {
    if (isLoading) {
      return [{ value: "", label: "Loading..." }];
    }

    if (isError) {
      return [{ value: "", label: "Error loading vehicle types" }];
    }

    return vehicles.map((vehicle: Vehicle) => ({
      value: vehicle.id,
      label: vehicle.vehicleName,
    }));
  }, [vehicles, isLoading, isError]);

  const handleValueChange = (option: VehicleOption) => {
    onChange?.(option.value);
  };

  // Helper functions for MySelect
  const getValue = (option: VehicleOption) => option.value;
  const getName = (option: VehicleOption) => option.label;

  return (
    <div className={cn("w-full")}>
      <MySelect<VehicleOption>
        name={name}
        id={id}
        label={showLabel ? "Vehicle" : ""}
        options={vehicleTypeOptions}
        getValue={getValue}
        getName={getName}
        value={value}
        onChange={onChange}
        onValChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled || isLoading || isError}
        className={cn(
          "focus:ring-primary focus:border-primary",
          {
            "opacity-50": isLoading,
          },
          className
        )}
        showSearch={true}
      />

      {isError && (
        <p className="mt-1 text-sm text-primary" role="alert">
          {getErrorDetails(errorData).message || "Error loading vehicle types"}
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default VehicleDropdownMenu;
